import React, { useState } from 'react';
import { registerUser } from '../services/dataService';

function Register({ onRegisterSuccess, onClose, onSwitchToLogin }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [licenseKey, setLicenseKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (password !== confirmPassword) {
            setError('Mật khẩu xác nhận không khớp');
            setLoading(false);
            return;
        }

        if (!licenseKey.trim()) {
            setError('Vui lòng nhập mã license');
            setLoading(false);
            return;
        }

        try {
            const result = await registerUser(username, password, licenseKey);
            if (result.success) {
                if (onRegisterSuccess) onRegisterSuccess();
            } else {
                setError(result.error || 'Đăng ký thất bại');
            }
        } catch (error) {
            console.error('Registration error:', error);
            setError('Đã xảy ra lỗi khi đăng ký');
        } finally {
            setLoading(false);
        }
    };

    const getPasswordStrength = (pass) => {
        if (!pass) return { strength: 0, label: '', color: '' };
        if (pass.length < 6) return { strength: 1, label: 'Yếu', color: 'bg-red-500' };
        if (pass.length < 10) return { strength: 2, label: 'Trung bình', color: 'bg-yellow-500' };
        return { strength: 3, label: 'Mạnh', color: 'bg-green-500' };
    };

    const passwordStrength = getPasswordStrength(password);

    return (
        <div className="fixed inset-0 bg-gradient-to-br from-blue-900/95 via-blue-800/95 to-blue-600/95 backdrop-blur-sm flex justify-center items-center z-50 animate-fadeIn p-2 sm:p-4 overflow-y-auto">
            <div className="p-4 sm:p-8 rounded-3xl shadow-2xl relative w-full max-w-md my-auto bg-white/95 backdrop-blur-md border-2 border-white/20 max-h-[95vh] overflow-y-auto">

                {/* Header with Icon */}
                <div className="text-center mb-4 sm:mb-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full mb-2 sm:mb-3 shadow-lg transform hover:rotate-12 transition-transform duration-300">
                        <span className="text-2xl sm:text-3xl">✨</span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-700 to-blue-500 bg-clip-text text-transparent">
                        Đăng ký
                    </h2>
                    <p className="text-gray-500 mt-1 text-sm sm:text-base">Tạo tài khoản mới để bắt đầu!</p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 text-red-700 px-3 py-2 rounded-lg mb-3 sm:mb-4 flex items-center space-x-2 animate-shake">
                        <span className="text-lg">⚠️</span>
                        <span className="font-medium text-sm">{error}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                    {/* Username Field */}
                    <div className="group">
                        <label className="block text-xs sm:text-sm font-semibold mb-1 sm:mb-2 text-gray-700 flex items-center space-x-1">
                            <span className="text-sm">👤</span>
                            <span>Tên đăng nhập</span>
                        </label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white text-gray-800 transition-all duration-200 group-hover:border-gray-300"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Nhập tên đăng nhập"
                            required
                        />
                    </div>

                    {/* Password Field */}
                    <div className="group">
                        <label className="block text-xs sm:text-sm font-semibold mb-1 sm:mb-2 text-gray-700 flex items-center space-x-1">
                            <span className="text-sm">🔑</span>
                            <span>Mật khẩu</span>
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                className="w-full px-3 py-2 pr-10 sm:px-4 sm:py-3 sm:pr-12 text-sm sm:text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white text-gray-800 transition-all duration-200 group-hover:border-gray-300"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Nhập mật khẩu (tối thiểu 6 ký tự)"
                                required
                                minLength={6}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <span className="text-xl">{showPassword ? '👁️' : '👁️‍🗨️'}</span>
                            </button>
                        </div>
                        {/* Password Strength Indicator */}
                        {password && (
                            <div className="mt-1 sm:mt-2">
                                <div className="flex items-center space-x-2">
                                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full ${passwordStrength.color} transition-all duration-300`}
                                            style={{ width: `${(passwordStrength.strength / 3) * 100}%` }}
                                        ></div>
                                    </div>
                                    <span className={`text-xs font-semibold ${passwordStrength.color.replace('bg-', 'text-')}`}>
                                        {passwordStrength.label}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password Field */}
                    <div className="group">
                        <label className="block text-xs sm:text-sm font-semibold mb-1 sm:mb-2 text-gray-700 flex items-center space-x-1">
                            <span className="text-sm">🔐</span>
                            <span>Xác nhận mật khẩu</span>
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                className="w-full px-3 py-2 pr-10 sm:px-4 sm:py-3 sm:pr-12 text-sm sm:text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white text-gray-800 transition-all duration-200 group-hover:border-gray-300"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Nhập lại mật khẩu"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <span className="text-xl">{showConfirmPassword ? '👁️' : '👁️‍🗨️'}</span>
                            </button>
                        </div>
                        {/* Password Match Indicator */}
                        {confirmPassword && (
                            <div className="mt-1 sm:mt-2 flex items-center space-x-1">
                                {password === confirmPassword ? (
                                    <>
                                        <span className="text-green-500">✓</span>
                                        <span className="text-xs text-green-600 font-medium">Mật khẩu khớp</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="text-red-500">✗</span>
                                        <span className="text-xs text-red-600 font-medium">Mật khẩu không khớp</span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* License Key Field */}
                    <div className="group">
                        <label className="block text-xs sm:text-sm font-semibold mb-1 sm:mb-2 text-gray-700 flex items-center space-x-1">
                            <span className="text-sm">🎫</span>
                            <span>Mã License</span>
                            <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white text-gray-800 transition-all duration-200 group-hover:border-gray-300"
                            value={licenseKey}
                            onChange={(e) => setLicenseKey(e.target.value)}
                            placeholder="Nhập mã license để kích hoạt"
                            required
                        />
                        <p className="text-xs mt-1 text-gray-500 flex items-center space-x-1">
                            <span className="text-xs">ℹ️</span>
                            <span>Mã license là bắt buộc</span>
                        </p>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        className={`w-full py-3 sm:py-4 px-4 sm:px-6 text-base sm:text-lg font-bold bg-gradient-to-r from-blue-700 to-blue-500 hover:from-blue-800 hover:to-blue-600 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                            loading ? 'opacity-70 cursor-not-allowed' : ''
                        }`}
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center space-x-2">
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Đang xử lý...</span>
                            </span>
                        ) : (
                            <span className="flex items-center justify-center space-x-2">
                                <span>Tạo tài khoản</span>
                                <span>✨</span>
                            </span>
                        )}
                    </button>
                </form>

                {/* Footer */}
                <div className="mt-3 sm:mt-4 text-center">
                    <p className="text-xs sm:text-sm text-gray-500">
                        Đã có tài khoản? <span 
                            onClick={onSwitchToLogin}
                            className="text-blue-600 font-semibold cursor-pointer hover:underline"
                        >
                            Đăng nhập ngay
                        </span>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Register;